/**
 * Retry auth operations with exponential backoff
 * Handles 409 and 504 errors gracefully
 */
export async function retryAuthOperation<T>(
  operation: () => Promise<T>,
  maxRetries = 3
): Promise<T | null> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      const status = error?.status || error?.code;
      const isFetchError = error?.message?.includes('Failed to fetch') || error?.name === 'TypeError';
      
      // Don't retry on 409 - use cache instead
      if (status === 409) {
        console.warn('[AuthRetry] 409 detected - too many concurrent requests, using cache');
        return { error: { message: 'Too many concurrent requests. Please try again.' } } as T;
      }

      // Retry on network failures, 503 (database unavailable), and 504 (timeout)
      if ((isFetchError || [503, 504].includes(status)) && attempt < maxRetries - 1) {
        const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
        console.log(`[AuthRetry] Network error - retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Log other errors
      console.error('[AuthRetry] Operation failed:', error);
      throw error;
    }
  }
  return { error: { message: 'Service temporarily unavailable. Please try again.' } } as T;
}
